import { type RefObject, useEffect, useState } from 'react';

export const useCoachmarkAnchorReady = (
  open: boolean,
  ref: RefObject<HTMLElement | null>,
) => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let frameId = 0;

    if (!open) {
      setIsReady(false);
      return;
    }

    setIsReady(false);

    let frameCount = 0;
    let stableFrameCount = 0;
    let previousRect: CoachmarkAnchorRect | null = null;

    const checkAnchor = () => {
      const rect = ref.current?.getBoundingClientRect();
      const measuredRect =
        rect && rect.width > 0 && rect.height > 0
          ? toCoachmarkAnchorRect(rect)
          : null;

      frameCount += 1;

      if (measuredRect) {
        stableFrameCount =
          previousRect &&
          isCoachmarkAnchorRectStable(previousRect, measuredRect)
            ? stableFrameCount + 1
            : 0;
        previousRect = measuredRect;

        if (
          stableFrameCount >= COACHMARK_ANCHOR_STABLE_FRAME_COUNT ||
          frameCount >= COACHMARK_ANCHOR_READY_FRAME_LIMIT
        ) {
          setIsReady(true);
          return;
        }
      }

      if (frameCount < COACHMARK_ANCHOR_READY_FRAME_LIMIT) {
        frameId = requestAnimationFrame(checkAnchor);
      }
    };

    frameId = requestAnimationFrame(checkAnchor);

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [open, ref]);

  return isReady;
};

type CoachmarkAnchorRect = Pick<
  DOMRectReadOnly,
  'x' | 'y' | 'width' | 'height'
>;

const COACHMARK_ANCHOR_STABLE_FRAME_COUNT = 2;
const COACHMARK_ANCHOR_READY_FRAME_LIMIT = 12;
const COACHMARK_ANCHOR_RECT_THRESHOLD = 0.5;

const toCoachmarkAnchorRect = (rect: DOMRectReadOnly): CoachmarkAnchorRect => ({
  x: rect.x,
  y: rect.y,
  width: rect.width,
  height: rect.height,
});

const isCoachmarkAnchorRectStable = (
  previousRect: CoachmarkAnchorRect,
  nextRect: CoachmarkAnchorRect,
) =>
  Math.abs(previousRect.x - nextRect.x) <= COACHMARK_ANCHOR_RECT_THRESHOLD &&
  Math.abs(previousRect.y - nextRect.y) <= COACHMARK_ANCHOR_RECT_THRESHOLD &&
  Math.abs(previousRect.width - nextRect.width) <=
    COACHMARK_ANCHOR_RECT_THRESHOLD &&
  Math.abs(previousRect.height - nextRect.height) <=
    COACHMARK_ANCHOR_RECT_THRESHOLD;
